import { apiFetch, apiFetchBlob, apiUploadFormData } from "@/lib/api";

import type { CorrectiveActionSource } from "./correctiveActionsApi";

export interface QualityNcrLink {
  id: number;
  issue: string | null;
  issueDate: string | null;
  type: string | null;
  pcbaSerial: string | null;
  clampSerial: string | null;
  deviceLabelSerial: string | null;
  repairScrap: string | null;
  open: boolean;
}

export interface QualityEventRca {
  id: number;
  status: string;
  problemStatement: string | null;
  createdAt: string;
  qualityCaseId: number | null;
}

export type QualityEventStatus = "open" | "closed";

export interface QualityEvent {
  id: number;
  createdAt: string;
  source: string | null;
  sourceCategory: CorrectiveActionSource | null;
  supplierId: number | null;
  supplierName: string | null;
  status: QualityEventStatus;
  summary: string | null;
  qualityCaseId: number | null;
  qualityCaseTitle: string | null;
  ncrs: QualityNcrLink[];
  rcas: QualityEventRca[];
  freshdeskTicketIds: number[];
}

export interface QualityEventMessage {
  id: string;
  createdAt: string;
  authorUserId: string;
  authorEmail: string;
  authorDisplayName: string;
  body: string;
}

export interface QualityEventListResponse {
  rows: QualityEvent[];
  totalCount: number;
  limit: number;
  offset: number;
}

export interface QualityNcrEventLink {
  id: number;
  source: string | null;
  sourceCategory: CorrectiveActionSource | null;
  supplierId: number | null;
  supplierName: string | null;
  status: QualityEventStatus;
  summary: string | null;
  qualityCaseId: number | null;
}

export interface QualityNcrUnitReading {
  id: number;
  unitSerial: string | null;
  unitModel: string | null;
  aaVoltage: string | null;
  cVoltage: string | null;
  hybridCap: string | null;
  firmwareVersion: string | null;
  boardRevision: string | null;
  visualInspection: string | null;
  boxLabel: string | null;
  loggedBy: string | null;
  receivedAt: string | null;
}

export type QualityNcrStatus = "unresolved" | "potting_rework" | "rework_failed" | "resolved";
export type QualityNcrDisposition = "repair" | "scrap" | "touch_sample";
export type QualityNcrListSort = "id" | "status" | "disposition";

export interface QualityNcrListRow {
  id: number;
  status: QualityNcrStatus;
  disposition: QualityNcrDisposition | null;
}

export interface QualityNcrListResponse {
  rows: QualityNcrListRow[];
  totalCount: number;
  limit: number;
  offset: number;
}

export interface QualityNcrRmaArtifact {
  rmaId: number;
  rmaNumber: string | null;
  intakeId: number | null;
  unitSerial: string | null;
  unitModel: string | null;
  receivedAt: string | null;
  aaVoltage: string | null;
  cVoltage: string | null;
  hybridCap: string | null;
  firmwareVersion: string | null;
  boardRevision: string | null;
  visualInspection: string | null;
  rcaNotes: string | null;
  resolution: string | null;
  loggedBy: string | null;
  boxLabel: string | null;
}

export interface QualityNcrRcaLink {
  id: number;
  status: string;
  problemStatement: string | null;
  qualityCaseId: number | null;
  createdAt: string;
}

export interface QualityNcr {
  id: number;
  issue: string | null;
  issueNotes: string | null;
  issueDate: string | null;
  type: string | null;
  pcbaSerial: string | null;
  clampSerial: string | null;
  deviceLabelSerial: string | null;
  finishedSku: string | null;
  productSku: string | null;
  repairScrap: string | null;
  resolutionDate: string | null;
  qualityCaseId: number | null;
  event: QualityNcrEventLink | null;
  rmaArtifacts: QualityNcrRmaArtifact[];
  unitReadings: QualityNcrUnitReading[];
  rcas: QualityNcrRcaLink[];
}

export function qualityEventLabel(id: number): string {
  return `Quality event ${id}`;
}

export function qualityNcrLabel(id: number): string {
  return `NCR ${id}`;
}

export function qualityEventStatusLabel(status: QualityEventStatus): string {
  return status === "closed" ? "Closed" : "Open";
}

export function qualityNcrQueryKey(id: number) {
  return ["quality", "ncr", id] as const;
}

export function qualityEventQueryKey(id: number) {
  return ["quality", "event", id] as const;
}

export function qualityEventMessagesQueryKey(id: number) {
  return ["quality", "event", id, "messages"] as const;
}

export interface QualityEventListFilters {
  search?: string;
  qualityCaseId?: number;
  status?: QualityEventStatus;
  sourceCategory?: CorrectiveActionSource;
  limit?: number;
  offset?: number;
}

function buildEventListQuery(filters: QualityEventListFilters): string {
  const params = new URLSearchParams();
  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.qualityCaseId != null) params.set("qualityCaseId", String(filters.qualityCaseId));
  if (filters.status != null) params.set("status", filters.status);
  if (filters.sourceCategory != null) params.set("sourceCategory", filters.sourceCategory);
  if (filters.limit != null) params.set("limit", String(filters.limit));
  if (filters.offset != null) params.set("offset", String(filters.offset));
  const query = params.toString();
  return query.length > 0 ? `?${query}` : "";
}

export async function fetchQualityEvents(
  filters: QualityEventListFilters = {},
): Promise<QualityEventListResponse> {
  return apiFetch<QualityEventListResponse>(`/api/quality/events${buildEventListQuery(filters)}`);
}

export async function fetchQualityEvent(id: number): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${id}`);
}

export interface QualityNcrListFilters {
  search?: string;
  status?: QualityNcrStatus[];
  disposition?: QualityNcrDisposition[];
  sort?: QualityNcrListSort;
  direction?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

function buildNcrListQuery(filters: QualityNcrListFilters): string {
  const params = new URLSearchParams();
  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.status != null && filters.status.length > 0) params.set("status", filters.status.join(","));
  if (filters.disposition != null && filters.disposition.length > 0) {
    params.set("disposition", filters.disposition.join(","));
  }
  if (filters.sort != null) params.set("sort", filters.sort);
  if (filters.direction != null) params.set("direction", filters.direction);
  if (filters.limit != null) params.set("limit", String(filters.limit));
  if (filters.offset != null) params.set("offset", String(filters.offset));
  const query = params.toString();
  return query.length > 0 ? `?${query}` : "";
}

export async function fetchQualityNcrs(
  filters: QualityNcrListFilters = {},
): Promise<QualityNcrListResponse> {
  return apiFetch<QualityNcrListResponse>(`/api/quality/ncrs${buildNcrListQuery(filters)}`);
}

export async function fetchQualityNcr(id: number): Promise<QualityNcr> {
  return apiFetch<QualityNcr>(`/api/quality/ncrs/${id}`);
}

export async function createQualityRcaForNcr(
  qualityIncidentId: number,
  problemStatement: string | null,
): Promise<{ rca: { id: number } }> {
  return apiFetch<{ rca: { id: number } }>("/api/quality/rcas", {
    method: "POST",
    body:
      problemStatement == null || problemStatement.trim() === ""
        ? { qualityIncidentId }
        : { qualityIncidentId, problemStatement: problemStatement.trim() },
  });
}

export async function linkQualityRcaToNcr(
  rcaId: number,
  qualityIncidentId: number,
): Promise<{ rca: { id: number } }> {
  return apiFetch<{ rca: { id: number } }>(`/api/quality/rcas/${rcaId}/links`, {
    method: "POST",
    body: { qualityIncidentId },
  });
}

/* NCR device worksheet and photos. The per-device investigation record lives on the NCR. */

export interface QualityNcrDeviceWorkResponse {
  qualityIncidentId: number;
  work: Record<string, unknown>;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface QualityNcrDevicePhoto {
  id: number;
  qualityIncidentId: number;
  filename: string;
  contentType: string;
  byteSize: number;
  uploadedBy: string;
  createdAt: string;
}

export function qualityNcrDeviceWorkQueryKey(ncrId: number): readonly unknown[] {
  return ["quality", "ncr", ncrId, "device-work"];
}

export function qualityNcrPhotosQueryKey(ncrId: number): readonly unknown[] {
  return ["quality", "ncr", ncrId, "photos"];
}

export async function fetchQualityNcrDeviceWork(ncrId: number): Promise<QualityNcrDeviceWorkResponse> {
  return apiFetch<QualityNcrDeviceWorkResponse>(`/api/quality/ncrs/${ncrId}/device-work`);
}

export async function saveQualityNcrDeviceWork(ncrId: number, work: object): Promise<QualityNcrDeviceWorkResponse> {
  return apiFetch<QualityNcrDeviceWorkResponse>(`/api/quality/ncrs/${ncrId}/device-work`, {
    method: "PATCH",
    body: { work },
  });
}

export async function fetchQualityNcrPhotos(ncrId: number): Promise<{ photos: QualityNcrDevicePhoto[] }> {
  return apiFetch<{ photos: QualityNcrDevicePhoto[] }>(`/api/quality/ncrs/${ncrId}/photos`);
}

export async function uploadQualityNcrPhoto(ncrId: number, file: File): Promise<{ photo: QualityNcrDevicePhoto }> {
  const body = new FormData();
  body.append("file", file);
  return apiUploadFormData(`/api/quality/ncrs/${ncrId}/photos`, body);
}

export async function fetchQualityNcrPhotoBlob(
  ncrId: number,
  photoId: number,
): Promise<{ blob: Blob; contentType: string }> {
  return apiFetchBlob(`/api/quality/ncrs/${ncrId}/photos/${photoId}`);
}

export async function deleteQualityNcrPhoto(ncrId: number, photoId: number): Promise<{ ok: boolean }> {
  return apiFetch(`/api/quality/ncrs/${ncrId}/photos/${photoId}`, { method: "DELETE" });
}

export async function addQualityNcrUnitReading(
  ncrId: number,
  input: {
    unitSerial?: string;
    unitModel?: string;
    aaVoltage?: string;
    cVoltage?: string;
    hybridCap?: string;
    firmwareVersion?: string;
    boardRevision?: string;
    visualInspection?: string;
    boxLabel?: string;
  },
): Promise<QualityNcr> {
  return apiFetch<QualityNcr>(`/api/quality/ncrs/${ncrId}/readings`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateQualityEventStatus(id: number, status: QualityEventStatus): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function fetchQualityEventMessages(eventId: number): Promise<{ messages: QualityEventMessage[] }> {
  return apiFetch<{ messages: QualityEventMessage[] }>(`/api/quality/events/${eventId}/messages`);
}

export async function addQualityEventMessage(
  eventId: number,
  body: string,
): Promise<{ messages: QualityEventMessage[] }> {
  return apiFetch<{ messages: QualityEventMessage[] }>(`/api/quality/events/${eventId}/messages`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

export async function linkQualityEventNcr(eventId: number, qualityIncidentId: number): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${eventId}/ncrs`, {
    method: "POST",
    body: JSON.stringify({ qualityIncidentId }),
  });
}

export async function createQualityEventNcr(eventId: number, issue: string): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${eventId}/ncrs`, {
    method: "POST",
    body: JSON.stringify({ issue }),
  });
}

export async function linkQualityEventCase(eventId: number, qualityCaseId: number): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${eventId}/case`, {
    method: "POST",
    body: JSON.stringify({ qualityCaseId }),
  });
}

export async function createQualityCaseForEvent(title: string, sourceType: CorrectiveActionSource | null): Promise<number> {
  const created = await apiFetch<{ case: { id: number } }>("/api/quality/cases", {
    method: "POST",
    body: JSON.stringify({ title, sourceType: sourceType ?? "other" }),
  });
  return created.case.id;
}

export async function addQualityEventFreshdeskTicket(eventId: number, freshdeskTicketId: number): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${eventId}/freshdesk-tickets`, {
    method: "POST",
    body: JSON.stringify({ freshdeskTicketId }),
  });
}

export async function removeQualityEventFreshdeskTicket(eventId: number, freshdeskTicketId: number): Promise<QualityEvent> {
  return apiFetch<QualityEvent>(`/api/quality/events/${eventId}/freshdesk-tickets/${freshdeskTicketId}`, {
    method: "DELETE",
  });
}
